/**
 * FahrzeugDetail — Detail-Seite für ein einzelnes Fahrzeug
 * Route: /fahrzeuge/:id/:section?
 * Pattern: analog zu AkteDetail — URL-basierte Tab-Navigation mit Sticky-Header
 *
 * Issue #577 — Fahrzeugverwaltung Stufe 2
 */

import { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'wouter';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  Car, ChevronRight, Home, Loader2, FileText,
  Wrench, BarChart2, TrendingUp, AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import { FahrzeugLabelGroup, deriveLabels } from '@/components/fahrzeug/FahrzeugLabel';
import type { FahrzeugLabelTyp } from '@/components/fahrzeug/FahrzeugLabel';
import VerkaufsTimingOptimizer from '@/components/fahrzeug/VerkaufsTimingOptimizer';
import DokumenteTab from '@/components/fahrzeug/DokumenteTab';
import {
  berechneAuslastungsQuote,
  berechneTageVermietet,
  berechneUmsatzYTD,
  berechneDurchschnittTagessatz,
  berechneHeatmapDaten,
  type VermietungsAkte,
} from '@/lib/fahrzeug-auslastung';
import { buildFinanzSektionen, type FahrzeugDetailFinanzdaten } from '@/lib/fahrzeug-detail-finanzen';

const KOOP_RED = '#C41E3A';

interface Fahrzeug {
  id: string;
  marke: string;
  modell: string;
  variante?: string;
  farbe?: string;
  kennzeichen?: string;
  fin?: string;
  erstzulassung?: string;
  kaufpreis_netto?: number;
  kaufpreis_brutto?: number;
  bruttolistenpreis?: number;
  anschaffungsdatum?: string;
  finanzierungsart?: string;
  finanzierungspartner?: string;
  monatliche_rate?: number;
  restschuld?: number;
  laufzeitende?: string;
  afa_beginn?: string;
  afa_nutzungsdauer_jahre?: number;
  restbuchwert?: number;
  versicherung_gesellschaft?: string;
  versicherung_police_nr?: string;
  versicherung_jahreswechsel?: string;
  buchhaltungskonto?: string;
  darlehensnummer?: string;
  hu_naechste?: string;
  km_aktuell?: number;
  status: string;
  betrieb: string;
  kategorie: string;
  mietklasse?: string;
  tagessatz_netto?: number;
  liefertermin?: string;
  notizen?: string;
  bild_url?: string;
  created_at: string;
  verkauft_am?: string;
  verkauft_preis_brutto?: number;
  verkauft_an?: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  bestellt:   { label: 'Bestellt',    color: '#F59E0B', bg: '#FEF3C7' },
  aktiv:      { label: 'Aktiv',       color: '#10B981', bg: '#D1FAE5' },
  vermietet:  { label: 'Vermietet',   color: '#1D4ED8', bg: '#DBEAFE' },
  werkstatt:  { label: 'Werkstatt',   color: '#EF4444', bg: '#FEE2E2' },
  reserviert: { label: 'Reserviert',  color: '#6366F1', bg: '#E0E7FF' },
  verkauft:   { label: 'Verkauft',    color: '#6B7280', bg: '#F3F4F6' },
  stillgelegt:{ label: 'Stillgelegt', color: '#9CA3AF', bg: '#F9FAFB' },
};

const KATEGORIE_LABELS: Record<string, string> = {
  pkw: 'PKW', transporter: 'Transporter', anhaenger: 'Anhänger', motorrad: 'Motorrad', sonstige: 'Sonstige',
};

const TABS = [
  { id: 'uebersicht',  label: 'Übersicht' },
  { id: 'vermietungen',label: 'Vermietungen' },
  { id: 'dokumente',   label: 'Dokumente' },
  { id: 'wartung',     label: 'Wartung' },
  { id: 'finanzen',    label: 'Finanzen' },
  { id: 'verkauf',     label: 'Verkauf' },
  { id: 'schaeden',    label: 'Schäden' },
  { id: 'notizen',     label: 'Notizen' },
] as const;

type TabId = typeof TABS[number]['id'];

const IMPLEMENTED_TABS: TabId[] = ['uebersicht', 'verkauf', 'vermietungen', 'dokumente', 'finanzen'];

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatCurrency(val?: number | null): string {
  if (val == null) return '—';
  return val.toLocaleString('de-DE', { minimumFractionDigits: 2 }) + ' €';
}

// ─── Heatmap-Helpers ─────────────────────────────────────────────────────────

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function localIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function HeatmapGrid({
  akten,
  aktenFull,
  monate,
}: {
  akten: VermietungsAkte[];
  aktenFull: { id: string; mietbeginn: string | null; mietende: string | null }[];
  monate: number;
}) {
  const [, setLocation] = useLocation();
  const today = new Date();
  const bis = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const von = new Date(bis);
  von.setMonth(von.getMonth() - monate + 1);
  von.setDate(1);

  const vonIso = localIsoDate(von);
  const bisIso = localIsoDate(bis);
  const todayIso = localIsoDate(today);
  const tage = berechneHeatmapDaten(akten, vonIso, bisIso);

  const TYP_STYLE: Record<string, string> = {
    vermietet: 'bg-green-500',
    frei:      'bg-gray-100',
  };

  function findAkteIdForDay(datum: string): string | null {
    for (const akte of aktenFull) {
      if (!akte.mietbeginn) continue;
      const beginn = akte.mietbeginn.slice(0, 10);
      const ende = akte.mietende ? akte.mietende.slice(0, 10) : todayIso;
      if (beginn <= datum && datum <= ende) return akte.id;
    }
    return null;
  }

  // Monatsgruppen für Labels
  const monate12: { label: string; tage: typeof tage }[] = [];
  let current: typeof tage = [];
  let currentMonth = '';
  for (const tag of tage) {
    const m = tag.datum.slice(0, 7);
    if (m !== currentMonth) {
      if (current.length > 0) monate12.push({ label: currentMonth, tage: current });
      currentMonth = m;
      current = [];
    }
    current.push(tag);
  }
  if (current.length > 0) monate12.push({ label: currentMonth, tage: current });

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-1 min-w-max">
        {monate12.map(({ label, tage: mt }) => {
          const d = new Date(label + '-01');
          const monatLabel = d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
          return (
            <div key={label} className="flex flex-col">
              <div className="text-[9px] font-bold text-gray-400 mb-1 uppercase tracking-wide">{monatLabel}</div>
              <div className="flex flex-wrap gap-0.5" style={{ width: Math.ceil(mt.length / 7) * 14 }}>
                {mt.map(tag => {
                  const akteId = tag.typ === 'vermietet' ? findAkteIdForDay(tag.datum) : null;
                  return (
                    <div
                      key={tag.datum}
                      title={tag.datum}
                      className={`w-2.5 h-2.5 rounded-sm transition-opacity hover:opacity-70 ${TYP_STYLE[tag.typ]}${akteId ? ' cursor-pointer' : ''}`}
                      onClick={akteId ? () => setLocation(`/akten/${akteId}`) : undefined}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-2">
        <span className="flex items-center gap-1 text-[9px] text-gray-400">
          <span className="w-2.5 h-2.5 rounded-sm bg-gray-100 inline-block" /> Frei
        </span>
        <span className="flex items-center gap-1 text-[9px] text-gray-400">
          <span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" /> Vermietet
        </span>
      </div>
    </div>
  );
}

// ─── Vermietungen-Tab ─────────────────────────────────────────────────────────

interface ForderungsAkte {
  id: string;
  akten_nr: string | null;
  akten_nummer?: string | null;
  kunde_name: string | null;
  mietbeginn: string | null;
  mietende: string | null;
  mietdauer_tage: number | null;
  tagespreis?: number | null;
  gesamtbetrag: number | null;
}

function VermietungenTab({ fahrzeugId }: { fahrzeugId: string }) {
  const [akten, setAkten] = useState<ForderungsAkte[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('forderungsakten')
        .select('id, akten_nr, akten_nummer, kunde_name, mietbeginn, mietende, mietdauer_tage, tagespreis, gesamtbetrag')
        .eq('fahrzeug_id', fahrzeugId)
        .not('mietbeginn', 'is', null)
        .order('mietbeginn', { ascending: false });
      if (cancelled) return;
      if (error) toast.error('Vermietungen konnten nicht geladen werden');
      else setAkten(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fahrzeugId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
      </div>
    );
  }

  const vermietungsAkten: VermietungsAkte[] = akten.map(a => ({
    mietbeginn: a.mietbeginn,
    mietende: a.mietende,
    gesamtbetrag: a.gesamtbetrag,
  }));

  const today = new Date();
  const since30  = new Date(today); since30.setDate(since30.getDate() - 30);
  const since90  = new Date(today); since90.setDate(since90.getDate() - 90);
  const since365 = new Date(today); since365.setDate(since365.getDate() - 365);

  const tage30  = berechneTageVermietet(vermietungsAkten, since30);
  const tage90  = berechneTageVermietet(vermietungsAkten, since90);
  const tage365 = berechneTageVermietet(vermietungsAkten, since365);
  const quote30  = berechneAuslastungsQuote(tage30, 30);
  const quote90  = berechneAuslastungsQuote(tage90, 90);
  const quote365 = berechneAuslastungsQuote(tage365, 365);
  const umsatzYTD = berechneUmsatzYTD(vermietungsAkten, today.getFullYear());
  const avgTagessatz = berechneDurchschnittTagessatz(vermietungsAkten);

  const statCards = [
    { label: 'Tage vermietet (30d)',   value: `${tage30} Tage`,   sub: `${quote30} %` },
    { label: 'Tage vermietet (90d)',   value: `${tage90} Tage`,   sub: `${quote90} %` },
    { label: 'Tage vermietet (365d)',  value: `${tage365} Tage`,  sub: `${quote365} %` },
    { label: 'Auslastung 30d',         value: `${quote30} %`,     sub: `${tage30} Tage vermietet` },
    { label: 'Ø Tagessatz',            value: avgTagessatz > 0 ? `${avgTagessatz.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—', sub: 'Netto' },
    { label: 'Umsatz YTD',             value: umsatzYTD > 0 ? `${umsatzYTD.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—', sub: String(today.getFullYear()) },
  ];

  return (
    <div className="space-y-4">
      {/* Auslastungs-Heatmap */}
      <div
        data-testid="vermietungen-heatmap"
        className="bg-white rounded-xl border border-gray-100 p-4"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
      >
        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
          Auslastung letzte 12 Monate
        </h3>
        <HeatmapGrid akten={vermietungsAkten} aktenFull={akten} monate={12} />
      </div>

      {/* Statistik-Cards */}
      <div
        data-testid="vermietungen-stats"
        className="grid grid-cols-2 sm:grid-cols-3 gap-3"
      >
        {statCards.slice(0, 3).map(card => (
          <div
            key={card.label}
            className="bg-white rounded-xl border border-gray-100 p-3 text-center"
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
          >
            <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{card.label}</div>
            <div className="text-lg font-bold text-gray-800 mt-1">{card.value}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">{card.sub} Auslastung</div>
          </div>
        ))}
        <div
          className="bg-white rounded-xl border border-gray-100 p-3 text-center"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Auslastung 30d</div>
          <div className="text-lg font-bold text-gray-800 mt-1">{quote30} %</div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
            <div
              className="h-1.5 rounded-full transition-all"
              style={{ width: `${Math.min(quote30, 100)}%`, backgroundColor: '#C41E3A' }}
            />
          </div>
        </div>
        <div
          className="bg-white rounded-xl border border-gray-100 p-3 text-center"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Ø Tagessatz</div>
          <div className="text-lg font-bold text-gray-800 mt-1">
            {avgTagessatz > 0 ? `${avgTagessatz.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—'}
          </div>
          <div className="text-[10px] text-gray-400 mt-0.5">Netto</div>
        </div>
        <div
          className="bg-white rounded-xl border border-gray-100 p-3 text-center"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Umsatz YTD</div>
          <div className="text-lg font-bold text-gray-800 mt-1">
            {umsatzYTD > 0
              ? `${umsatzYTD.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`
              : '—'}
          </div>
          <div className="text-[10px] text-gray-400 mt-0.5">{today.getFullYear()}</div>
        </div>
      </div>

      {/* Vermietungs-Liste */}
      <div
        data-testid="vermietungen-liste"
        className="bg-white rounded-xl border border-gray-100 overflow-hidden"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
      >
        <div className="px-4 py-3 border-b border-gray-50">
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Mietverträge ({akten.length})
          </h3>
        </div>
        {akten.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <BarChart2 className="w-8 h-8 text-gray-200 mb-2" />
            <p className="text-sm text-gray-400">Noch keine Vermietungen erfasst</p>
            <p className="text-xs text-gray-300 mt-1">Mietakten müssen diesem Fahrzeug zugeordnet sein</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-50">
                  {['Zeitraum', 'Mieter', 'Tage', 'Tagessatz', 'Betrag', 'Akte'].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-[9px] font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {akten.map(a => {
                  const nr = a.akten_nummer || a.akten_nr;
                  return (
                    <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                        {a.mietbeginn ? formatDate(a.mietbeginn) : '—'}
                        {a.mietende ? ` – ${formatDate(a.mietende)}` : ' – laufend'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700 font-medium max-w-[150px] truncate">
                        {a.kunde_name || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 text-right">
                        {a.mietdauer_tage ?? '—'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 text-right whitespace-nowrap">
                        {a.tagespreis != null
                          ? `${a.tagespreis.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`
                          : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700 font-medium text-right whitespace-nowrap">
                        {a.gesamtbetrag != null
                          ? `${a.gesamtbetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`
                          : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        {nr ? (
                          <a
                            href={`/akten/${a.id}`}
                            onClick={e => e.stopPropagation()}
                            className="flex items-center gap-1 text-[10px] font-mono text-blue-600 hover:underline"
                          >
                            {nr} <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Übersicht-Tab ────────────────────────────────────────────────────────────

interface FahrzeugStats {
  auslastung_30d: number;
  umsatz_ytd: number;
  aktuell_vermietet_bis: string | null;
  aktuell_mieter_name: string | null;
  aktuell_akten_nr: string | null;
}

function UebersichtTab({ fz, fahrzeugId }: { fz: Fahrzeug; fahrzeugId: string }) {
  const sc = STATUS_CONFIG[fz.status] || STATUS_CONFIG.aktiv;
  const labels = deriveLabels(fz) as FahrzeugLabelTyp[];
  const [stats, setStats] = useState<FahrzeugStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('v_fahrzeug_vermietungs_stats')
        .select('tage_vermietet_30d, umsatz_ytd, aktuell_vermietet_bis, aktuell_mieter_name, aktuell_akten_nr')
        .eq('fahrzeug_id', fahrzeugId)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        const tage30 = (data as any).tage_vermietet_30d ?? 0;
        setStats({
          auslastung_30d: berechneAuslastungsQuote(tage30, 30),
          umsatz_ytd: (data as any).umsatz_ytd ?? 0,
          aktuell_vermietet_bis: (data as any).aktuell_vermietet_bis ?? null,
          aktuell_mieter_name: (data as any).aktuell_mieter_name ?? null,
          aktuell_akten_nr: (data as any).aktuell_akten_nr ?? null,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [fahrzeugId]);

  const stammdaten: [string, string][] = [
    ['Marke', fz.marke],
    ['Modell', fz.modell],
    ['Variante', fz.variante || '—'],
    ['Farbe', fz.farbe || '—'],
    ['Kennzeichen', fz.kennzeichen || '—'],
    ['FIN', fz.fin || '—'],
    ['Erstzulassung', formatDate(fz.erstzulassung)],
    ['Kategorie', KATEGORIE_LABELS[fz.kategorie] || fz.kategorie],
    ['Betrieb', fz.betrieb],
    ['Mietklasse', fz.mietklasse || '—'],
    ['Tagessatz netto', formatCurrency(fz.tagessatz_netto)],
    ['KM-Stand', fz.km_aktuell != null ? fz.km_aktuell.toLocaleString('de-DE') + ' km' : '—'],
    ['Nächste HU', formatDate(fz.hu_naechste)],
    ['Versicherung', fz.versicherung_gesellschaft || '—'],
    ['Versicherungs-Nr.', fz.versicherung_police_nr || '—'],
    ['Kaufpreis brutto', formatCurrency(fz.kaufpreis_brutto)],
    ['Finanzierung', fz.finanzierungsart || '—'],
    ['Bank', fz.finanzierungspartner || '—'],
    ['Monatl. Rate', formatCurrency(fz.monatliche_rate)],
    ['Laufzeitende', formatDate(fz.laufzeitende)],
  ].filter(([, v]) => v !== '—') as [string, string][];

  return (
    <div className="space-y-4">
      {/* Status + Labels */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center justify-between">
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</h3>
          <span
            data-testid="fahrzeug-status-pill"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
            style={{ backgroundColor: sc.bg, color: sc.color }}
          >
            {sc.label}
          </span>
        </div>
        {labels.length > 0 && (
          <div>
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Labels</h3>
            <FahrzeugLabelGroup labels={labels} max={6} size="sm" />
          </div>
        )}
      </div>

      {/* Quick-KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <BarChart2 className="w-4 h-4 mx-auto mb-1 text-gray-300" />
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Auslastung 30d</div>
          <div className="text-sm font-bold text-gray-700 mt-0.5">
            {stats ? `${stats.auslastung_30d} %` : '—'}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <TrendingUp className="w-4 h-4 mx-auto mb-1 text-gray-300" />
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Umsatz YTD</div>
          <div className="text-sm font-bold text-gray-700 mt-0.5">
            {stats && stats.umsatz_ytd > 0
              ? `${stats.umsatz_ytd.toLocaleString('de-DE', { minimumFractionDigits: 0 })} €`
              : '—'}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <Wrench className="w-4 h-4 mx-auto mb-1 text-gray-300" />
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Letzter Service</div>
          <div className="text-sm font-bold text-gray-700 mt-0.5">—</div>
        </div>
      </div>

      {/* Aktuell vermietet Banner */}
      {stats?.aktuell_vermietet_bis && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3">
          <TrendingUp className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            Aktuell vermietet bis{' '}
            <strong>{formatDate(stats.aktuell_vermietet_bis)}</strong>
            {stats.aktuell_mieter_name && ` an ${stats.aktuell_mieter_name}`}
            {stats.aktuell_akten_nr && (
              <span className="ml-1 text-[11px] font-mono text-blue-500">— {stats.aktuell_akten_nr}</span>
            )}
          </p>
        </div>
      )}

      {/* Stammdaten-Grid */}
      <div
        data-testid="fahrzeug-stammdaten"
        className="bg-white rounded-xl border border-gray-100 p-4"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
      >
        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Stammdaten</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {stammdaten.map(([label, value]) => (
            <div key={label}>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">{label}</div>
              <div className="text-sm text-gray-700 font-medium">{value}</div>
            </div>
          ))}
        </div>
        {fz.notizen && (
          <div className="mt-3 pt-3 border-t border-gray-50">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Notizen</div>
            <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{fz.notizen}</p>
          </div>
        )}
      </div>

      {/* HU-Warnung */}
      {fz.hu_naechste && (() => {
        const hu = new Date(fz.hu_naechste);
        const today = new Date();
        const in60Days = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
        if (hu > in60Days) return null;
        return (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-700">
              HU fällig am {formatDate(fz.hu_naechste)} — bitte zeitnah einen Termin vereinbaren.
            </p>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Finanzen-Tab ─────────────────────────────────────────────────────────────

function FinanzenDetailTab({ fz }: { fz: Fahrzeug }) {
  const finanzdaten: FahrzeugDetailFinanzdaten = {
    kaufpreis_netto: fz.kaufpreis_netto ?? null,
    kaufpreis_brutto: fz.kaufpreis_brutto ?? null,
    bruttolistenpreis: fz.bruttolistenpreis ?? null,
    finanzierungspartner: fz.finanzierungspartner ?? null,
    finanzierungsart: fz.finanzierungsart ?? null,
    monatliche_rate: fz.monatliche_rate ?? null,
    restschuld: fz.restschuld ?? null,
    laufzeitende: fz.laufzeitende ?? null,
    anschaffungsdatum: fz.anschaffungsdatum ?? null,
    afa_beginn: fz.afa_beginn ?? null,
    afa_nutzungsdauer_jahre: fz.afa_nutzungsdauer_jahre ?? null,
    restbuchwert: fz.restbuchwert ?? null,
    versicherung_gesellschaft: fz.versicherung_gesellschaft ?? null,
    versicherung_police_nr: fz.versicherung_police_nr ?? null,
    versicherung_jahreswechsel: fz.versicherung_jahreswechsel ?? null,
    buchhaltungskonto: fz.buchhaltungskonto ?? null,
    darlehensnummer: fz.darlehensnummer ?? null,
  };

  const sektionen = buildFinanzSektionen(finanzdaten);

  if (sektionen.length === 0) {
    return (
      <div
        data-testid="fahrzeug-finanzen-leer"
        className="flex flex-col items-center justify-center py-16 text-center"
      >
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
          <TrendingUp className="w-5 h-5 text-gray-300" />
        </div>
        <h3 className="text-sm font-semibold text-gray-500">Keine Finanzdaten hinterlegt</h3>
        <p className="text-xs text-gray-400 mt-1">Kaufpreis, Finanzierung und AfA werden hier angezeigt.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="fahrzeug-finanzen">
      {sektionen.map(sektion => (
        <div
          key={sektion.titel}
          className="bg-white rounded-xl border border-gray-100 p-4"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
            {sektion.titel}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sektion.zeilen.map(zeile => (
              <div key={zeile.label}>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                  {zeile.label}
                </div>
                <div className="text-sm text-gray-700 font-medium">{zeile.value}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── In-Entwicklung-Platzhalter ───────────────────────────────────────────────

function InEntwicklungTab({ label }: { label: string }) {
  return (
    <div
      data-testid="tab-in-entwicklung"
      className="flex flex-col items-center justify-center py-16 text-center"
    >
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
        <FileText className="w-5 h-5 text-gray-300" />
      </div>
      <h3 className="text-sm font-semibold text-gray-500">{label}</h3>
      <p className="text-xs text-gray-400 mt-1">Dieser Bereich ist in Entwicklung.</p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FahrzeugDetail() {
  const params = useParams<{ id: string; section?: string }>();
  const fahrzeugId = params.id;
  const section = (params.section || 'uebersicht') as TabId;
  const [, setLocation] = useLocation();

  const [fahrzeug, setFahrzeug] = useState<Fahrzeug | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function loadFahrzeug() {
    if (!fahrzeugId) return;
    const { data, error: err } = await supabase
      .from('fahrzeuge')
      .select('*')
      .eq('id', fahrzeugId)
      .single();
    if (err) {
      setError(err.message);
      toast.error('Fahrzeug konnte nicht geladen werden');
    } else {
      setFahrzeug(data as Fahrzeug);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (!fahrzeugId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error: err } = await supabase
        .from('fahrzeuge')
        .select('*')
        .eq('id', fahrzeugId)
        .single();
      if (cancelled) return;
      if (err) {
        setError(err.message);
        toast.error('Fahrzeug konnte nicht geladen werden');
      } else {
        setFahrzeug(data as Fahrzeug);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fahrzeugId]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Fahrzeug wird geladen...</span>
      </div>
    );
  }

  if (error || !fahrzeug) {
    return (
      <div className="p-8 text-center min-h-[200px] flex flex-col items-center justify-center">
        <Car className="w-8 h-8 text-gray-300 mb-2" />
        <p className="text-sm font-medium text-gray-500">Fahrzeug nicht gefunden</p>
        <button
          onClick={() => setLocation('/fahrzeuge')}
          className="mt-3 text-sm hover:underline font-medium"
          style={{ color: KOOP_RED }}
        >
          Zurück zur Fahrzeugliste
        </button>
      </div>
    );
  }

  const sc = STATUS_CONFIG[fahrzeug.status] || STATUS_CONFIG.aktiv;
  const title = [fahrzeug.marke, fahrzeug.modell, fahrzeug.variante].filter(Boolean).join(' ');
  const activeTab = TABS.find(t => t.id === section) ?? TABS[0];

  return (
    <div className="flex flex-col h-full">
      {/* ── Breadcrumb-Leiste ─────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 lg:px-5 py-2 flex items-center gap-2 flex-shrink-0 flex-wrap">
        <nav className="flex items-center gap-1 text-[11px] sm:text-[12px] text-gray-400 flex-shrink-0">
          <Link href="/" className="hover:text-gray-600 transition-colors">
            <Home className="w-3.5 h-3.5" />
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link href="/fahrzeuge" className="hover:text-gray-600 transition-colors">Fahrzeuge</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-700 font-medium truncate">{title}</span>
        </nav>

        <span
          className="text-[9px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: sc.bg, color: sc.color }}
        >
          {sc.label}
        </span>

        {fahrzeug.kennzeichen && (
          <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-gray-100 rounded text-gray-700 flex-shrink-0">
            {fahrzeug.kennzeichen}
          </span>
        )}
      </div>

      {/* ── Scrollbarer Inhalt ────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">

        {/* Sticky Header + Sub-Nav */}
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200" data-testid="fahrzeug-detail-header">
          {/* Fahrzeug-Header */}
          <div className="px-3 sm:px-4 lg:px-5 py-3 flex items-center gap-3">
            {fahrzeug.bild_url ? (
              <img
                src={fahrzeug.bild_url}
                alt={title}
                className="w-12 h-10 object-cover rounded-lg flex-shrink-0 border border-gray-100"
              />
            ) : (
              <div
                className="w-12 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-100"
                style={{ backgroundColor: 'rgba(196,30,58,0.06)' }}
              >
                <Car className="w-5 h-5" style={{ color: KOOP_RED }} />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <h1 className="text-sm sm:text-base font-bold text-gray-900 truncate">{title}</h1>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                {fahrzeug.kennzeichen && (
                  <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-gray-100 rounded text-gray-700">
                    {fahrzeug.kennzeichen}
                  </span>
                )}
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: sc.bg, color: sc.color }}
                >
                  {sc.label}
                </span>
                <span className="text-[10px] text-gray-400 font-medium">
                  {KATEGORIE_LABELS[fahrzeug.kategorie] || fahrzeug.kategorie}
                </span>
              </div>
            </div>
          </div>

          {/* Sub-Navigation */}
          <nav
            data-testid="fahrzeug-subnav"
            className="flex items-center gap-0 px-3 sm:px-4 lg:px-5 overflow-x-auto scrollbar-none"
          >
            {TABS.map(tab => {
              const isActive = tab.id === section;
              return (
                <button
                  key={tab.id}
                  data-active={isActive}
                  onClick={() => setLocation(`/fahrzeuge/${fahrzeugId}/${tab.id}`)}
                  className={`flex-shrink-0 px-3 py-2.5 text-[12px] font-semibold border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-[#C41E3A] text-[#C41E3A]'
                      : 'border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab-Inhalt */}
        <div className="p-3 sm:p-4 lg:p-5">
          {section === 'uebersicht' ? (
            <UebersichtTab fz={fahrzeug} fahrzeugId={fahrzeug.id} />
          ) : section === 'verkauf' ? (
            <VerkaufsTimingOptimizer fahrzeugId={fahrzeug.id} />
          ) : section === 'vermietungen' ? (
            <VermietungenTab fahrzeugId={fahrzeug.id} />
          ) : section === 'dokumente' ? (
            <DokumenteTab
              fahrzeugId={fahrzeug.id}
              fahrzeug={fahrzeug as unknown as Record<string, unknown>}
              onFahrzeugUpdated={loadFahrzeug}
            />
          ) : section === 'finanzen' ? (
            <FinanzenDetailTab fz={fahrzeug} />
          ) : (
            <InEntwicklungTab label={activeTab.label} />
          )}
        </div>
      </div>
    </div>
  );
}
